import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarClock,
  Check,
  Crown,
  ExternalLink,
  Phone,
  Send,
  type LucideIcon,
} from "lucide-react";
import { useToast } from "@/shared/ui/Toast";
import { Input } from "@/shared/ui/Input";
import { useSettings, useUpdateSettings } from "./hooks";
import { SettingsError } from "./api";

export function SettingsScreen() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useSettings();

  return (
    <div className="max-w-[640px]">
      {isError ? (
        <p className="text-[13.5px] font-medium text-red">{t("settings.loadError")}</p>
      ) : isLoading || !data ? (
        <div className="flex flex-col gap-5">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {/* Each card owns its field, dirty-check and partial PATCH — saving one never
              touches the others' values. */}
          <PremiumPriceCard value={data.premium_price} />
          <SupportLinkCard value={data.support_telegram_link} />
          <SupportPhoneCard value={data.support_phone} />
          <FreeTierCard value={data.free_tier_months} />
        </div>
      )}
    </div>
  );
}

// Group the integer part in 3s for readability ("50000" → "50 000"); an optional
// decimal tail is left intact. State holds the raw digits — this is display-only.
function groupThousands(raw: string): string {
  if (raw === "") return "";
  const [int, dec] = raw.split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return dec === undefined ? grouped : `${grouped}.${dec}`;
}

function PremiumPriceCard({ value }: { value: string | null }) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const update = useUpdateSettings();

  const [input, setInput] = useState(value ?? "");
  const [error, setError] = useState<string | null>(null);

  // Re-sync when the persisted value changes (a successful save primes the cache).
  useEffect(() => {
    setInput(value ?? "");
    setError(null);
  }, [value]);

  const trimmed = input.trim();
  const dirty = trimmed !== (value ?? "");
  const valid =
    trimmed !== "" && Number.isFinite(Number(trimmed)) && Number(trimmed) >= 0;

  async function onSave() {
    if (!dirty || !valid) return;
    setError(null);
    try {
      await update.mutateAsync({ premium_price: trimmed });
      showToast(t("settings.saved"));
    } catch (e) {
      const code = e instanceof SettingsError ? e.code : "generic";
      setError(t(code === "invalidPrice" ? "settings.priceInvalid" : "settings.saveError"));
    }
  }

  return (
    <SettingCard
      icon={Crown}
      title={t("settings.premiumTitle")}
      description={t("settings.premiumHint")}
      saving={update.isPending}
      canSave={dirty && valid}
      dirty={dirty}
      onSave={onSave}
      onRevert={() => {
        setInput(value ?? "");
        setError(null);
      }}
      error={error}
      renderField={({ labelledBy, describedBy, invalid }) => (
        <div className="relative">
          {/* type="text" (not number): number inputs can't render thousands separators. */}
          <Input
            type="text"
            inputMode="numeric"
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={groupThousands(input)}
            onChange={(e) => {
              setInput(e.target.value.replace(/[^\d.]/g, ""));
              setError(null);
            }}
            placeholder="0"
            invalid={invalid}
            className={`tnum h-[44px] pl-3.5 pr-16 text-[15px] text-grey-900 ${
              dirty ? "border-warning bg-warning-bg" : ""
            }`}
          />
          <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[13.5px] font-medium text-grey-400">
            {t("settings.currencySuffix")}
          </span>
        </div>
      )}
    />
  );
}

function SupportLinkCard({ value }: { value: string | null }) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const update = useUpdateSettings();

  const [input, setInput] = useState(value ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setInput(value ?? "");
    setError(null);
  }, [value]);

  const trimmed = input.trim();
  const dirty = trimmed !== (value ?? "");
  // Optional (can be cleared) — but if present it must look like an http(s) URL.
  const valid = trimmed === "" || /^https?:\/\//i.test(trimmed);

  async function onSave() {
    if (!dirty || !valid) return;
    setError(null);
    try {
      await update.mutateAsync({ support_telegram_link: trimmed });
      showToast(t("settings.saved"));
    } catch (e) {
      const code = e instanceof SettingsError ? e.code : "generic";
      setError(t(code === "invalidUrl" ? "settings.linkInvalid" : "settings.saveError"));
    }
  }

  return (
    <SettingCard
      icon={Send}
      title={t("settings.supportTitle")}
      description={t("settings.supportHint")}
      saving={update.isPending}
      canSave={dirty && valid}
      dirty={dirty}
      onSave={onSave}
      onRevert={() => {
        setInput(value ?? "");
        setError(null);
      }}
      error={error}
      renderField={({ labelledBy, describedBy, invalid }) => (
        <div>
          <Input
            type="url"
            inputMode="url"
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setError(null);
            }}
            placeholder="https://t.me/nasiyah_support"
            invalid={invalid}
            className={`h-[44px] px-3.5 text-[15px] text-grey-900 ${
              dirty ? "border-warning bg-warning-bg" : ""
            }`}
          />
          {/* Open the typed link in a new tab so the operator can verify it lands on the
              right Telegram profile before saving. Shown only while the URL is valid. */}
          {trimmed !== "" && valid && (
            <a
              href={trimmed}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2.5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-green-dark hover:text-green"
            >
              <ExternalLink size={14} strokeWidth={2} aria-hidden />
              {t("settings.testLink")}
            </a>
          )}
        </div>
      )}
    />
  );
}

function normalizeSupportPhone(raw: string): string | null {
  if (!/^\+?[\d\s().-]+$/.test(raw)) return null;
  const digits = raw.replace(/\D/g, "");
  const canonicalDigits = digits.length === 9 ? `998${digits}` : digits;
  if (!/^[1-9]\d{7,14}$/.test(canonicalDigits)) return null;
  return `+${canonicalDigits}`;
}

function SupportPhoneCard({ value }: { value: string | null }) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const update = useUpdateSettings();

  const [input, setInput] = useState(value ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setInput(value ?? "");
    setError(null);
  }, [value]);

  const trimmed = input.trim();
  const normalized = trimmed === "" ? "" : normalizeSupportPhone(trimmed);
  const dirty = (normalized ?? trimmed) !== (value ?? "");
  const valid = normalized !== null;
  const fieldError = error ?? (dirty && !valid ? t("settings.phoneInvalid") : null);

  async function onSave() {
    if (!dirty || !valid) return;
    setError(null);
    try {
      await update.mutateAsync({ support_phone: normalized });
      showToast(t("settings.saved"));
    } catch (e) {
      const code = e instanceof SettingsError ? e.code : "generic";
      setError(
        t(code === "invalidPhone" ? "settings.phoneInvalid" : "settings.saveError"),
      );
    }
  }

  return (
    <SettingCard
      icon={Phone}
      title={t("settings.phoneTitle")}
      description={t("settings.phoneHint")}
      saving={update.isPending}
      canSave={dirty && valid}
      dirty={dirty}
      onSave={onSave}
      onRevert={() => {
        setInput(value ?? "");
        setError(null);
      }}
      error={fieldError}
      renderField={({ labelledBy, describedBy, invalid }) => (
        <Input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          aria-labelledby={labelledBy}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
          }}
          placeholder="+998 93 385 26 00"
          invalid={invalid}
          className={`h-[44px] px-3.5 text-[15px] text-grey-900 ${
            dirty ? "border-warning bg-warning-bg" : ""
          }`}
        />
      )}
    />
  );
}

function FreeTierCard({ value }: { value: number }) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const update = useUpdateSettings();

  // State holds the raw string; the persisted value is a number.
  const [input, setInput] = useState(String(value));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setInput(String(value));
    setError(null);
  }, [value]);

  const trimmed = input.trim();
  const dirty = trimmed !== String(value);
  // Whole number of months, at least 1 (mirrors the backend min_value=1).
  const valid = /^\d+$/.test(trimmed) && Number(trimmed) >= 1;

  async function onSave() {
    if (!dirty || !valid) return;
    setError(null);
    try {
      await update.mutateAsync({ free_tier_months: Number(trimmed) });
      showToast(t("settings.saved"));
    } catch (e) {
      const code = e instanceof SettingsError ? e.code : "generic";
      setError(
        t(code === "invalidMonths" ? "settings.monthsInvalid" : "settings.saveError"),
      );
    }
  }

  return (
    <SettingCard
      icon={CalendarClock}
      title={t("settings.freeTierTitle")}
      description={t("settings.freeTierHint")}
      saving={update.isPending}
      canSave={dirty && valid}
      dirty={dirty}
      onSave={onSave}
      onRevert={() => {
        setInput(String(value));
        setError(null);
      }}
      error={error}
      renderField={({ labelledBy, describedBy, invalid }) => (
        <div className="relative">
          <Input
            type="text"
            inputMode="numeric"
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={input}
            onChange={(e) => {
              // Digits only — months is a plain whole number.
              setInput(e.target.value.replace(/[^\d]/g, ""));
              setError(null);
            }}
            placeholder="6"
            invalid={invalid}
            className={`tnum h-[44px] pl-3.5 pr-12 text-[15px] text-grey-900 ${
              dirty ? "border-warning bg-warning-bg" : ""
            }`}
          />
          <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[13.5px] font-medium text-grey-400">
            {t("settings.monthsSuffix")}
          </span>
        </div>
      )}
    />
  );
}

interface FieldA11y {
  // Wire these onto the input so the card title names it (1.3.1/4.1.2) and the
  // hint + error describe it; `invalid` reflects the current error state.
  labelledBy: string;
  describedBy: string;
  invalid: boolean;
}

interface SettingCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  renderField: (a: FieldA11y) => React.ReactNode;
  saving: boolean;
  canSave: boolean;
  dirty: boolean;
  onSave: () => void;
  onRevert: () => void;
  error: string | null;
}

function SettingCard({
  icon: Icon,
  title,
  description,
  renderField,
  saving,
  canSave,
  dirty,
  onSave,
  onRevert,
  error,
}: SettingCardProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const errorId = useId();
  // The input is always described by the hint, plus the error when one is shown.
  const describedBy = error ? `${descId} ${errorId}` : descId;

  return (
    <section className="rounded-card border border-grey-200 bg-white p-6 shadow-card">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-control bg-green-bg text-green-dark">
          <Icon size={20} strokeWidth={1.8} />
        </div>
        <div>
          {/* h2: sits under the topbar h1 — no heading-level skip. */}
          <h2 id={titleId} className="text-[15px] font-bold text-grey-900">{title}</h2>
          <p id={descId} className="mt-0.5 text-[13px] text-grey-500">{description}</p>
        </div>
      </div>

      {/* A real form so Enter in the field saves (preventDefault stops a page reload). */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave && !saving) onSave();
        }}
      >
        <div className="mb-4">{renderField({ labelledBy: titleId, describedBy, invalid: !!error })}</div>

        {error && (
          <p id={errorId} role="alert" className="mb-3 text-[13px] font-medium text-red">
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-3">
          {dirty && (
            // Non-color cue for unsaved state (the dot alone would fail WCAG 1.4.1).
            <span className="mr-auto flex items-center gap-1.5 text-[12.5px] font-medium text-warning">
              <span className="h-1.5 w-1.5 flex-none rounded-pill bg-warning" aria-hidden />
              {t("settings.unsaved")}
            </span>
          )}
          {dirty && (
            <button
              type="button"
              onClick={onRevert}
              disabled={saving}
              className="text-[13px] font-semibold text-grey-500 hover:text-grey-700 disabled:opacity-50"
            >
              {t("settings.revert")}
            </button>
          )}
          <button
            type="submit"
            disabled={!canSave || saving}
            className="flex h-10 items-center gap-2 rounded-control bg-green px-4 text-[14px] font-semibold text-white hover:bg-green-dark disabled:opacity-50"
          >
            <Check size={18} strokeWidth={2} />
            {saving ? t("settings.saving") : t("settings.save")}
          </button>
        </div>
      </form>
    </section>
  );
}

function CardSkeleton() {
  return (
    <div className="rounded-card border border-grey-200 bg-white p-6 shadow-card">
      <div className="mb-4 flex items-center gap-3">
        <div className="h-10 w-10 flex-none animate-pulse rounded-control bg-grey-100" />
        <div className="flex-1">
          <div className="h-4 w-40 animate-pulse rounded bg-grey-100" />
          <div className="mt-2 h-3 w-60 animate-pulse rounded bg-grey-100" />
        </div>
      </div>
      <div className="h-[44px] w-full animate-pulse rounded-control bg-grey-100" />
    </div>
  );
}
